import React, { createContext, useContext, useRef, useState } from 'react';

import type { ExtensionFieldId } from '@getjust/checkout-extensions';

import { useDeleteLineItems } from '$src/hooks/mutations/useDeleteLineItems';

interface ExtensionContextType {
  // Field values (checkbox states, etc.)
  fields: Record<ExtensionFieldId, unknown>;
  setField: (fieldId: ExtensionFieldId, value: unknown) => void;

  // Custom attributes from discount codes
  attributes: Array<{ key: string; value: string }>;
  addAttributes: (attributes: Array<{ key: string; value: string }>) => void;
  removeAttributes: (attributes: Array<{ key: string; value: string }>) => void;

  // Products added by discount codes
  discountProducts: Map<string, string[]>; // discountCode -> lineItemIds
  trackProducts: (discountCode: string, lineItemIds: string[]) => void;
  removeProducts: (discountCode: string, currentCartLineItemIds: string[]) => Promise<void>;
}

const ExtensionContext = createContext<ExtensionContextType | null>(null);

interface ExtensionProviderProps {
  children: React.ReactNode;
}

export const ExtensionProvider: React.FC<ExtensionProviderProps> = ({ children }) => {
  const [fields, setFields] = useState<Record<ExtensionFieldId, unknown>>({});
  const [attributes, setAttributes] = useState<Array<{ key: string; value: string }>>([]);
  const discountProducts = useRef<Map<string, string[]>>(new Map());
  const { mutateAsync: deleteLineItems } = useDeleteLineItems();

  const setField = (fieldId: ExtensionFieldId, value: unknown) => {
    console.warn(`[Extensions] Field ${fieldId} = ${value}`);
    setFields((prev) => ({ ...prev, [fieldId]: value }));
  };

  const addAttributes = (newAttributes: Array<{ key: string; value: string }>) => {
    console.warn(`[Extensions] Adding attributes:`, newAttributes);
    setAttributes((prev) => {
      const updated = [...prev];
      for (const attr of newAttributes) {
        const existingIndex = updated.findIndex((existing) => existing.key === attr.key);
        if (existingIndex >= 0) {
          updated[existingIndex] = attr; // Update
        } else {
          updated.push(attr); // Add
        }
      }
      return updated;
    });
  };

  const removeAttributes = (toRemove: Array<{ key: string; value: string }>) => {
    console.warn(`[Extensions] Removing attributes:`, toRemove);
    setAttributes((prev) =>
      prev.filter(
        (attr) => !toRemove.some((remove) => remove.key === attr.key && remove.value === attr.value),
      ),
    );
  };

  const trackProducts = (discountCode: string, lineItemIds: string[]) => {
    console.warn(`[Extensions] Tracking products for ${discountCode}:`, lineItemIds);
    discountProducts.current.set(discountCode, lineItemIds);
  };

  const removeProducts = async (discountCode: string, currentCartLineItemIds: string[]): Promise<void> => {
    const addedProducts = discountProducts.current.get(discountCode);
    if (!addedProducts?.length) return;

    // Only remove products that still exist in cart
    const inCartProducts = addedProducts.filter((id) => currentCartLineItemIds.includes(id));

    if (inCartProducts.length > 0) {
      console.warn(`[Extensions] Removing products for ${discountCode}:`, inCartProducts);
      try {
        await deleteLineItems({ lineItemIds: inCartProducts });
        console.warn(`[Extensions] Products removed for ${discountCode}`);
      } catch (error) {
        console.error(`[Extensions] Failed to remove products for ${discountCode}:`, error);
      }
    } else {
      console.warn(`[Extensions] No products to remove for ${discountCode} (manually removed)`);
    }

    // Clean up tracking
    discountProducts.current.delete(discountCode);
  };

  return (
    <ExtensionContext.Provider
      value={{
        fields,
        setField,
        attributes,
        addAttributes,
        removeAttributes,
        trackProducts,
        removeProducts,
        discountProducts: discountProducts.current,
      }}
    >
      {children}
    </ExtensionContext.Provider>
  );
};

/**
 * Hook to use the extension context
 */
export const useExtensionContext = (): ExtensionContextType => {
  const context = useContext(ExtensionContext);
  if (!context) {
    throw new Error('useExtensionContext must be used within ExtensionProvider');
  }
  return context;
};
